import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAppDispatch } from "@/redux/hooks";
import { openSnackbarAction } from "@/redux/slices";
import { routes } from "@/routes/utils";
import { Trash2 } from "lucide-react";
import {
  useGetSchoolDetailsQuery,
  useDownloadSchoolDetailsExcel,
} from "@/services/report";
import moment from "moment";
import { cn, reportStatus, reportCrmStatus } from "@/lib/utils";
import { Badge } from "@/components/ui";
import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";

const useSchool_Reports = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [filterData, setFilterData] = useState<any>(null);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  // Fetch school data
  const {
    data: schoolData,
    isLoading,
    error,
  } = useGetSchoolDetailsQuery(filterData);

  const { mutate: downloadExcel, isPending: isExcelDownloading } =
    useDownloadSchoolDetailsExcel();

  // Prepare table data
  const tableData = schoolData?.data?.items || [];
  const totalRecords = schoolData?.count || tableData.length;
  const totalPages = Math.ceil(totalRecords / pageSize);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogData, setDialogData] = useState<{
    title: string;
    values: string[];
  }>({
    title: "",
    values: [],
  });

  // Table Columns
  const columns = [
    { accessorKey: "schoolName", header: "School Name" },
    {
      accessorKey: "dealCloserDate",
      header: "Deal Closure",
      accessorFn: (record: any) =>
        record.dealCloserDate
          ? moment(record.dealCloserDate).format("DD-MM-YYYY")
          : "—",
    },
    { accessorKey: "regionName", header: "Region" },
    {
      accessorKey: "salesPerson",
      header: "Sales Person",
      accessorFn: () => "—",
    },
    {
      accessorKey: "crmRH",
      header: "CRM RH",
      accessorFn: (r: any) => r.crmRHName ?? "—",
    },
    {
      accessorKey: "crmRM",
      header: "CRM RM",
      accessorFn: (r: any) => r.crmRMName ?? "—",
    },
    {
      accessorKey: "crmStatus",
      header: <div>CRM Status</div>,
      cell: (row: any) => {
        const crmValue = row?.row?.original?.crmStatus;
        const status =
          crmValue === true ? "Yes" : crmValue === false ? "No" : "";
        const statusObj = (reportCrmStatus as any)[status];

        return (
          <Badge
            variant="outline"
            className={cn(
              statusObj?.color,
              statusObj?.bgColor,
              "rounded-[5px] px-4 py-2 border-none"
            )}
          >
            {statusObj?.label || status || "—"}
          </Badge>
        );
      },
    },
    {
      accessorKey: "accountType",
      header: "Account Type",
      accessorFn: (r: any) => r.accountType ?? "—",
    },
    { accessorKey: "schoolTier", header: "Tier" },
    { accessorKey: "state", header: "State" },
    { accessorKey: "city", header: "City" },
    {
      accessorKey: "gradesSelected",
      header: "Grades Selected",
      cell: ({ row }: any) => {
        const grades =
          row.original?.curriculumData?.map((c: any) => c.curriculum_name) ||
          [];
        return grades.length > 0 ? (
          <span
            onClick={() => {
              setDialogData({ title: "Grades Selected", values: grades });
              setOpenDialog(true);
            }}
            className="text-primary underline cursor-pointer"
          >
            View
          </span>
        ) : (
          "—"
        );
      },
    },
    { accessorKey: "sku", header: "SKU" },
    {
      accessorKey: "syllabus",
      header: "Syllabus",
      accessorFn: (r: any) => r.syllabus?.join(", ") || "—",
    },
    {
      accessorKey: "noOfWorkStations",
      header: "# Workstations",
      accessorFn: (r: any) => r.noOfWorkStations ?? "—",
    },
    {
      accessorKey: "facultyDays",
      header: "Faculty Days",
      cell: ({ row }: any) => {
        const facultyDays = row.original?.facultyDays || [];
        return facultyDays.length > 0 ? (
          <span
            onClick={() => {
              setDialogData({ title: "Faculty Days", values: facultyDays });
              setOpenDialog(true);
            }}
            className="text-primary underline cursor-pointer"
          >
            View
          </span>
        ) : (
          "—"
        );
      },
    },
    {
      accessorKey: "noOfFacultyDays",
      header: "# Faculty Days",
      accessorFn: (r: any) => r.noOfFacultyDays ?? "—",
    },
    {
      accessorKey: "contractYears",
      header: "Contract Years",
      accessorFn: (r:any) => r.contractYear ?? "—",
    },
    {
      accessorKey: "labSetupStatus",
      header: <div>Lab Setup Status</div>,
      cell: (row: any) => {
        const status = row?.row?.original?.labSetupStatus;
        const statusObj = (reportStatus as any)[status];
        return (
          <Badge
            variant="outline"
            className={cn(
              statusObj?.color,
              statusObj?.bgColor,
              "rounded-[5px] px-4 py-2 border-none"
            )}
          >
            {statusObj?.label || status || "—"}
          </Badge>
        );
      },
    },
    {
      accessorKey: "labSetupLead",
      header: "Lab Setup Lead",
      accessorFn: () => "—",
    },
    {
      accessorKey: "expectedLaunchDate",
      header: "Expected Launch Date",
       accessorFn: (record: any) =>
        record.expectedLaunchDate
          ? moment(record.expectedLaunchDate).format("DD-MM-YYYY")
          : "—",
    },
  ];

  // Handlers
  const onAdd = () => navigate(`${routes.predefinedTask}/${routes.add}`);
  const onRowClick = (row: any) => console.log("Row clicked:", row);

  useEffect(() => {
    const paramsObj: any = {};
    searchParams.forEach((value, key) => {
      paramsObj[key] = value;
    });
    if (Object.keys(paramsObj).length > 0) {
      setFilterData(paramsObj);
    }
  }, []);

  // 🔹 Whenever filterData changes, update URL params
  useEffect(() => {
    if (filterData) {
      const params = new URLSearchParams();
      Object.entries(filterData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          params.set(key, String(value));
        }
      });
      setSearchParams(params);
    } else {
      // Clear all query params when filters are cleared
      setSearchParams({});
    }
  }, [filterData]);

  const onCancelFilter = () => setOpenFilter(false);
  const onFilterBtnClick = () => setOpenFilter(true);
  const onFilterApply = (newFilterData: any) => {
    setFilterData(newFilterData);
    setPageNumber(0);
    setOpenFilter(false);
  };

  const handleDownloadExcel = async () => {
    await downloadExcel();
  };

  return {
    columns,
    onAdd,
    openDeleteDialog,
    setOpenDeleteDialog,
    onCancelFilter,
    onFilterBtnClick,
    onFilterApply,
    onRowClick,
    openFilter,
    filterData,
    setFilterData,
    tasksData: tableData,
    pageNumber,
    pageSize,
    totalPages,
    totalRecords,
    setPageNumber,
    setPageSize,
    isLoading,
    error,
    isDeleting: false,
    openDialog,
    setOpenDialog,
    dialogData,
    handleDownloadExcel,
    isExcelDownloading,
  };
};

export default useSchool_Reports;
